import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import type { Session as AuthSession } from "@supabase/supabase-js";
import { BarChart3, CalendarDays, Clock, ListChecks, Lock, LogOut, UserRound } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Button } from "@v2/components/ui/button";
import { supabaseV2 } from "../../lib/supabase";
import { formatDateTime } from "../../lib/format";
import { statusLabel, teamRoleLabel } from "../../lib/labels";
import { useSeason } from "../../lib/seasons";
import { SeasonSwitcher } from "../../components/shell/SeasonSwitcher";
import { EmptyState, FullScreenState, SectionLoading } from "../../components/state";
import { TeamCrest } from "../../components/primitives";
import { PickYourselfStep } from "../../components/PickYourselfStep";
import { FcIcon } from "../../components/brand";
import { AppFooter } from "../../components/shell/AppFooter";
import { AuthShell } from "../../components/shell/AuthShell";
import { currentIntlLocale } from "../../lib/i18n";
import { PushSubscribeManager } from "../settings/PushSubscribeManager";
import { AccountCard } from "../account/AccountCard";
import { MyTeamsCard } from "../account/MyTeamsCard";
import { configureSwNavigationBridge, refreshSubscriptionIfNeeded } from "../../lib/push";
import { PlayerEaTab } from "../player-detail/PlayerEaTab";
import { buildPlayerEaCandidates } from "../../lib/ea-name";
import { isOutOfSquad } from "../../lib/roster";
import type {
  MatchPlayerStat,
  Player,
  ResponseStatus,
  Session,
  SessionResponse,
  SquadScoreSetting,
  SquadScoreSnapshot,
  Team,
} from "../../types";

export function PlayerArea({
  session,
  team,
  role,
  onSignOut,
  onCreateNewTeam,
}: {
  session: AuthSession;
  team: Team;
  role: string;
  onSignOut: () => void;
  onCreateNewTeam?: () => void;
}) {
  const { t: tPa } = useTranslation("playerArea");
  const playerQuery = useQuery({
    queryKey: ["self-player", session.user.id, team.id],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("players")
        .select("*")
        .eq("team_id", team.id)
        .eq("user_id", session.user.id)
        .maybeSingle();
      if (error) throw error;
      return data as Player | null;
    },
  });

  if (playerQuery.isLoading) {
    return <FullScreenState title={tPa("loadingTitle")} description={tPa("loadingProfile")} />;
  }

  if (!playerQuery.data) {
    return (
      <UnlinkedPlayerScreen
        team={team}
        session={session}
        onSignOut={onSignOut}
      />
    );
  }

  return (
    <PlayerDashboard
      session={session}
      team={team}
      role={role}
      player={playerQuery.data}
      onSignOut={onSignOut}
      onCreateNewTeam={onCreateNewTeam}
    />
  );
}

type PlayerView = "todo" | "calendar" | "stats" | "ea" | "profile";

function PlayerDashboard({
  session,
  team,
  role,
  player,
  onSignOut,
  onCreateNewTeam,
}: {
  session: AuthSession;
  team: Team;
  role: string;
  player: Player;
  onSignOut: () => void;
  onCreateNewTeam?: () => void;
}) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const playerViewFromPath = (path: string): PlayerView => {
    const seg = path.replace(/^\/me\/?/, "").split("/")[0];
    if (seg === "calendar" || seg === "stats" || seg === "ea" || seg === "profile") return seg;
    return "todo";
  };
  const activeView: PlayerView = playerViewFromPath(location.pathname);
  const setActiveView = (v: PlayerView) => navigate(v === "todo" ? "/me" : `/me/${v}`);
  // Se siamo su un path staff (es. /home), normalizziamo a /me.
  useEffect(() => {
    if (!location.pathname.startsWith("/me")) {
      navigate("/me", { replace: true });
    }
  }, [location.pathname, navigate]);

  // Push notifications: bridge SW → router + sync subscription se gia' attiva.
  useEffect(() => {
    configureSwNavigationBridge((url) => navigate(url));
    refreshSubscriptionIfNeeded().catch(() => {});
  }, [navigate]);

  // Realtime: invalida le notifications in-app quando ne arriva una nuova.
  useEffect(() => {
    if (session.access_token) supabaseV2.realtime.setAuth(session.access_token);
    const channel = supabaseV2
      .channel(`eb-realtime-player-${session.user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications" }, () => {
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "session_callups" }, () => {
        queryClient.invalidateQueries({ queryKey: ["player-callups"] });
      })
      .subscribe();
    return () => { supabaseV2.removeChannel(channel); };
  }, [session.user.id, session.access_token, queryClient]);


  const eventsQuery = useQuery({
    queryKey: ["player-events", team.id],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("sessions")
        .select("*")
        .eq("team_id", team.id)
        .order("starts_at", { ascending: false });
      if (error) throw error;
      return (data || []) as Session[];
    },
  });
  const responsesQuery = useQuery({
    queryKey: ["player-responses", player.id],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("session_responses")
        .select("*")
        .eq("player_id", player.id);
      if (error) throw error;
      return (data || []) as SessionResponse[];
    },
  });
  const callupsQuery = useQuery({
    queryKey: ["player-callups", player.id],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("session_callups")
        .select("session_id")
        .eq("player_id", player.id)
        .eq("status", "confirmed");
      if (error) throw error;
      return (data || []).map((r: { session_id: string }) => r.session_id);
    },
  });
  const matchStatsQuery = useQuery({
    queryKey: ["player-match-stats", player.id],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("match_player_stats")
        .select("*, matches!inner(team_id, session_id, starts_at, opponent_name, our_score, opponent_score, counts_for_stats)")
        .eq("player_id", player.id)
        .eq("matches.team_id", team.id)
        .eq("matches.counts_for_stats", true);
      if (error) throw error;
      return (data || []).map((row: any) => ({
        id: row.id,
        match_id: row.match_id,
        player_id: row.player_id,
        goals: row.goals || 0,
        assists: row.assists || 0,
        yellow_cards: row.yellow_cards || 0,
        red_cards: row.red_cards || 0,
        started: !!row.started,
        match_starts_at: row.matches?.starts_at || null,
        match_opponent_name: row.matches?.opponent_name || null,
        match_our_score: row.matches?.our_score ?? null,
        match_opponent_score: row.matches?.opponent_score ?? null,
        match_session_id: row.matches?.session_id || null,
      })) as Array<MatchPlayerStat & { match_session_id: string | null }>;
    },
  });
  const snapshotsQuery = useQuery({
    queryKey: ["player-score-snapshots", player.id],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("squad_score_snapshots")
        .select("*")
        .eq("player_id", player.id)
        .order("period_end", { ascending: false });
      if (error) throw error;
      return (data || []) as SquadScoreSnapshot[];
    },
  });

  // Fuori rosa (status diverso da "active"): niente sondaggi. Le policy RLS
  // rifiutano comunque la scrittura, qui evitiamo anche la richiesta.
  const outOfSquad = isOutOfSquad(player);
  const respond = useMutation({
    mutationFn: async ({ eventId, status }: { eventId: string; status: ResponseStatus }) => {
      if (outOfSquad) throw new Error(tDash("todo.outOfSquadError"));
      const existing = (responsesQuery.data || []).find((r) => r.session_id === eventId);
      if (existing) {
        const { error } = await supabaseV2
          .from("session_responses")
          .update({
            status,
            source: "player_area",
            responded_at: new Date().toISOString(),
          })
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabaseV2.from("session_responses").insert({
          session_id: eventId,
          player_id: player.id,
          status,
          source: "player_area",
          responded_at: new Date().toISOString(),
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["player-responses", player.id] });
    },
  });

  const allEvents = eventsQuery.data || [];
  const allResponses = responsesQuery.data || [];
  const allMatchStats = matchStatsQuery.data || [];
  // Statistiche e calendario seguono la stagione selezionata (default: in corso).
  // "Da fare" resta su tutte le sessioni future.
  const { seasons, selectedSeason } = useSeason();
  const seasonBySession = new Map(allEvents.map((e) => [e.id, e.season_id]));
  const inSelectedSeason = (sessionId: string | null | undefined) =>
    !selectedSeason || (!!sessionId && seasonBySession.get(sessionId) === selectedSeason.id);
  const responses = allResponses.filter((r) => inSelectedSeason(r.session_id));
  const matchStats = allMatchStats.filter((s) => inSelectedSeason(s.match_session_id));
  const snapshots = snapshotsQuery.data || [];
  const calledUpSessionIds = new Set(callupsQuery.data || []);

  // Per i provinanti (player.is_guest=true) limitiamo le sessioni visibili a
  // quelle dove sono effettivamente coinvolti: sessioni con include_guests=true,
  // sessioni con callup, sessioni con risposta gia' tracciata.
  const involvedIds = new Set<string>([
    ...calledUpSessionIds,
    ...allResponses.map((r) => r.session_id),
  ]);
  const visibleEvents = player.is_guest
    ? allEvents.filter((e) => e.include_guests === true || involvedIds.has(e.id))
    : allEvents;
  const events = visibleEvents.filter((e) => inSelectedSeason(e.id));

  const responseByEvent = new Map(allResponses.map((r) => [r.session_id, r]));
  const now = Date.now();
  const upcoming = visibleEvents.filter((event) => new Date(event.starts_at).getTime() >= now);
  const todoEvents = outOfSquad
    ? []
    : upcoming.filter((event) => {
        const r = responseByEvent.get(event.id);
        return !r || r.status === "no_response";
      });
  const goals = matchStats.reduce((acc, s) => acc + (s.goals || 0), 0);
  const assists = matchStats.reduce((acc, s) => acc + (s.assists || 0), 0);
  const starts = matchStats.filter((s) => s.started).length;
  const present = responses.filter((r) => r.status === "present").length;
  const late = responses.filter((r) => r.status === "late").length;
  const absent = responses.filter((r) => r.status === "absent").length;
  const noResponse = upcoming.filter((event) => !responseByEvent.get(event.id)).length;
  const matchSessionIds = new Set(events.filter((e) => (e as Session & { has_matches?: boolean }).has_matches).map((e) => e.id));
  const matchPresent = responses.filter((r) => matchSessionIds.has(r.session_id) && (r.status === "present" || r.status === "late")).length;
  const trainingPresent = (present + late) - matchPresent;

  const { t: tDash } = useTranslation("playerArea");
  const playerNav: Array<{ id: PlayerView; label: string; icon: typeof BarChart3 }> = [
    { id: "todo", label: tDash("tabs.todo"), icon: ListChecks },
    { id: "calendar", label: tDash("tabs.calendar"), icon: CalendarDays },
    { id: "stats", label: tDash("tabs.stats"), icon: BarChart3 },
    { id: "ea", label: tDash("tabs.ea"), icon: FcIcon as typeof BarChart3 },
    { id: "profile", label: tDash("tabs.profile"), icon: UserRound },
  ];

  const roleLine = [team.name, player.shirt_number != null ? `#${player.shirt_number}` : null, player.role_code].filter(Boolean).join(" · ");
  return (
    <div className="min-h-screen flex flex-col pb-[calc(4rem+env(safe-area-inset-bottom))] lg:pb-0">
      <header className="max-w-3xl w-full mx-auto px-4 sm:px-6 pt-[max(1.25rem,env(safe-area-inset-top))]">
        <div className="flex items-center gap-3">
          <TeamCrest team={team} size={36} />
          <div className="min-w-0 flex-1">
            <h1 className="text-[22px] font-bold leading-tight truncate">{tDash("hello", { firstName: player.first_name })}</h1>
            <p className="text-[13px] text-[hsl(var(--faint))] truncate">{roleLine}</p>
          </div>
          {seasons.length > 1 && <SeasonSwitcher variant="compact" side="bottom" />}
          <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0" onClick={onSignOut} aria-label={tDash("signOut")} title={tDash("signOut")}>
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
        <dl className="mt-4 grid grid-cols-3 gap-2">
          {([
            [tDash("tilesTodo"), todoEvents.length, "dot-muted"],
            [tDash("tilesPresent"), present + late, "dot-ok"],
            [tDash("tilesAbsent"), absent, "dot-danger"],
          ] as const).map(([label, value, dot]) => (
            <div key={label} className="rounded-2xl bg-card p-3 min-w-0">
              <dd className="flex items-center gap-2"><span aria-hidden className={`eb-dot ${dot}`} /><span className="eb-num text-[34px] pr-1">{value}</span></dd>
              <dt className="mt-0.5 text-[13px] text-[hsl(var(--faint))] truncate">{label}</dt>
            </div>
          ))}
        </dl>
      </header>

      {/* Da desktop i tab stanno sopra il contenuto, su telefono e tablet in basso. */}
      <nav className="hidden lg:block max-w-3xl w-full mx-auto px-6 pt-5">
        <div className="flex gap-1.5">
          {playerNav.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveView(item.id)}
              aria-current={activeView === item.id ? "page" : undefined}
              className={cn("h-10 px-4 rounded-full text-sm transition-colors", activeView === item.id ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:bg-raised hover:text-foreground")}
            >
              {item.label}
            </button>
          ))}
        </div>
      </nav>
      <nav aria-label={tDash("tabs.label")} className="lg:hidden fixed inset-x-0 bottom-0 z-30 bg-background border-t pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto max-w-xl grid grid-cols-5">
          {playerNav.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveView(item.id)}
                aria-current={isActive ? "page" : undefined}
                className={cn("h-16 flex flex-col items-center justify-center gap-1 text-xs transition-colors", isActive ? "text-foreground font-semibold" : "text-[hsl(var(--faint))]")}
              >
                <Icon className="h-[22px] w-[22px]" strokeWidth={isActive ? 2.2 : 1.7} />
                {item.label}
              </button>
            );
          })}
        </div>
      </nav>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-5 space-y-6 animate-fade-in">
        {activeView === "todo" && (
          <PlayerTodoTab
            events={todoEvents}
            allUpcoming={outOfSquad ? [] : upcoming}
            outOfSquad={outOfSquad}
            responseByEvent={responseByEvent}
            calledUpSessionIds={calledUpSessionIds}
            onRespond={(eventId, status) => respond.mutate({ eventId, status })}
            isSaving={respond.isPending}
          />
        )}
        {activeView === "calendar" && (
          <PlayerCalendarTab events={events} responseByEvent={responseByEvent} />
        )}
        {activeView === "stats" && (
          <PlayerSelfStatsTab
            matchStats={matchStats}
            present={present}
            late={late}
            absent={absent}
            noResponse={noResponse}
            goals={goals}
            assists={assists}
            starts={starts}
            matchPresent={matchPresent}
            trainingPresent={trainingPresent}
            matchSessions={matchSessionIds.size}
            trainingSessions={events.length - matchSessionIds.size}
          />
        )}
        {activeView === "ea" && (
          <PlayerEaPersonalTab teamId={team.id} player={player} />
        )}
        {activeView === "profile" && (
          <PlayerProfileTab
            player={player}
            email={session.user.email || ""}
            userId={session.user.id}
            role={role}
            team={team}
            onCreateNewTeam={onCreateNewTeam}
          />
        )}
      </main>
      <AppFooter />
    </div>
  );
}

function PlayerTodoTab({
  events,
  allUpcoming,
  responseByEvent,
  calledUpSessionIds,
  onRespond,
  isSaving,
  outOfSquad = false,
}: {
  events: Session[];
  allUpcoming: Session[];
  responseByEvent: Map<string, SessionResponse>;
  calledUpSessionIds: Set<string>;
  onRespond: (eventId: string, status: ResponseStatus) => void;
  isSaving: boolean;
  outOfSquad?: boolean;
}) {
  const { t } = useTranslation("playerArea");
  const calledUp = allUpcoming
    .filter((event) => calledUpSessionIds.has(event.id))
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  const otherTodo = [...events]
    .filter((event) => !calledUpSessionIds.has(event.id))
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  const respondedUpcoming = allUpcoming
    .filter((event) =>
      !calledUpSessionIds.has(event.id) &&
      responseByEvent.get(event.id) &&
      responseByEvent.get(event.id)!.status !== "no_response",
    )
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at))
    .slice(0, 5);

  if (outOfSquad) {
    return (
      <section className="rounded-[20px] bg-[hsl(var(--warn-soft))] p-5">
        <h2 className="text-[17px] font-semibold">{t("todo.outOfSquadTitle")}</h2>
        <p className="mt-1 text-muted-foreground">{t("todo.outOfSquadText")}</p>
      </section>
    );
  }

  return (
    <div className="space-y-6">
      {calledUp.length > 0 && (
        <section className="space-y-3">
          <SectionHead title={t("callups.title")} count={calledUp.length} />
          {calledUp.map((event) => (
            <PlayerSessionResponseCard
              key={event.id}
              event={event}
              currentStatus={responseByEvent.get(event.id)?.status || "no_response"}
              onRespond={(status) => onRespond(event.id, status)}
              disabled={isSaving}
              tone="callup"
              locked
            />
          ))}
        </section>
      )}

      <section className="space-y-3">
        <SectionHead title={t("todo.title")} count={otherTodo.length} />
        {otherTodo.map((event) => (
          <PlayerSessionResponseCard
            key={event.id}
            event={event}
            currentStatus={responseByEvent.get(event.id)?.status || "no_response"}
            onRespond={(status) => onRespond(event.id, status)}
            disabled={isSaving}
          />
        ))}
        {otherTodo.length === 0 && <EmptyState text={t("todo.emptyText")} />}
      </section>

      {respondedUpcoming.length > 0 && (
        <section className="space-y-3">
          <SectionHead title={t("responded.title")} count={respondedUpcoming.length} />
          {respondedUpcoming.map((event) => (
            <PlayerSessionResponseCard
              key={event.id}
              event={event}
              currentStatus={responseByEvent.get(event.id)!.status}
              onRespond={(status) => onRespond(event.id, status)}
              disabled={isSaving}
              compact
            />
          ))}
        </section>
      )}
    </div>
  );
}

function SectionHead({ title, count }: { title: string; count?: number }) {
  return (
    <h2 className="text-[17px] font-semibold px-1">
      {title}
      {count != null && count > 0 && <span className="ml-2 text-[13px] font-normal text-[hsl(var(--faint))]">{count}</span>}
    </h2>
  );
}

// Il segno di stato e' lo stesso ovunque: cerchio per presente, quadrato per
// ritardo e assente, contorno per chi non ha risposto.
const STATUS_DOT: Record<ResponseStatus, string> = { present: "dot-ok", late: "dot-warn", absent: "dot-danger", no_response: "dot-muted" };
const STATUS_ON: Record<ResponseStatus, string> = {
  present: "bg-[hsl(var(--ok))] text-background hover:bg-[hsl(var(--ok))]",
  late: "bg-[hsl(var(--warn))] text-background hover:bg-[hsl(var(--warn))]",
  absent: "bg-[hsl(var(--danger))] text-background hover:bg-[hsl(var(--danger))]",
  no_response: "",
};

function StatusTag({ status }: { status: ResponseStatus }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground whitespace-nowrap">
      <span aria-hidden className={`eb-dot ${STATUS_DOT[status]}`} />
      {statusLabel(status)}
    </span>
  );
}

function DateBlock({ iso }: { iso: string }) {
  const date = new Date(iso);
  const locale = currentIntlLocale();
  return (
    <span className="w-11 shrink-0 text-center">
      <span className="eb-data block text-[13px] text-[hsl(var(--faint))]">{date.toLocaleDateString(locale, { weekday: "short" }).replace(".", "")}</span>
      <span className="eb-num block text-[32px]">{date.getDate()}</span>
      <span className="eb-data block text-xs text-[hsl(var(--faint))]">{date.toLocaleDateString(locale, { month: "short" }).replace(".", "")}</span>
    </span>
  );
}

function PlayerSessionResponseCard({
  event,
  currentStatus,
  onRespond,
  disabled,
  compact = false,
  tone = "default",
  locked = false,
}: {
  event: Session;
  currentStatus: ResponseStatus;
  onRespond: (status: ResponseStatus) => void;
  disabled?: boolean;
  compact?: boolean;
  tone?: "default" | "callup";
  locked?: boolean;
}) {
  const { t } = useTranslation("playerArea");
  const time = new Date(event.starts_at).toLocaleTimeString(currentIntlLocale(), { hour: "2-digit", minute: "2-digit" });
  return (
    <div className="rounded-[20px] bg-card p-4">
      <div className="flex items-center gap-3">
        <DateBlock iso={event.starts_at} />
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-[hsl(var(--faint))]">
            <span>
              {event.has_matches ? t("session.matchDay") : t("session.training")} · {time}
              {event.location ? ` · ${event.location}` : ""}
            </span>
            {tone === "callup" && <span className="rounded-md bg-[hsl(var(--ok-soft))] px-2 text-[hsl(var(--ok))]">{t("session.calledUpBadge")}</span>}
          </p>
          <p className="font-semibold truncate">{event.title}</p>
        </div>
      </div>
      {locked ? (
        <>
          <div className="mt-3 flex items-center gap-2.5 rounded-xl bg-raised px-3 h-11">
            <span aria-hidden className={`eb-dot ${STATUS_DOT[currentStatus]}`} />
            <span className="flex-1 min-w-0 truncate font-medium">{t("callups.yourResponse", { status: statusLabel(currentStatus) })}</span>
            <Lock className="h-4 w-4 text-[hsl(var(--faint))]" />
          </div>
          <p className="mt-2.5 text-[13px] text-[hsl(var(--faint))]">{t("callups.lockNote")}</p>
        </>
      ) : (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {(["present", "late", "absent"] as ResponseStatus[]).map((next) => (
            <Button
              key={next}
              variant="outline"
              size={compact ? "sm" : "default"}
              disabled={disabled}
              onClick={() => onRespond(next)}
              aria-pressed={currentStatus === next}
              className={cn("rounded-xl px-2 gap-1.5", currentStatus === next && `${STATUS_ON[next]} font-semibold`)}
            >
              {currentStatus !== next && <span aria-hidden className={`eb-dot ${STATUS_DOT[next]}`} />}
              {statusLabel(next)}
            </Button>
          ))}
        </div>
      )}
      {event.response_deadline_at && !compact && (
        <p className="mt-2.5 flex items-center gap-1.5 text-[13px] text-muted-foreground">
          <Clock className="h-4 w-4 shrink-0 text-[hsl(var(--warn))]" />
          {t("session.deadline", { when: formatDateTime(event.response_deadline_at) })}
        </p>
      )}
    </div>
  );
}

function PlayerCalendarTab({
  events,
  responseByEvent,
}: {
  events: Session[];
  responseByEvent: Map<string, SessionResponse>;
}) {
  const { t } = useTranslation("playerArea");
  const now = Date.now();
  const upcoming = events
    .filter((event) => new Date(event.starts_at).getTime() >= now)
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  const recent = events
    .filter((event) => new Date(event.starts_at).getTime() < now)
    .sort((a, b) => b.starts_at.localeCompare(a.starts_at))
    .slice(0, 20);
  const list = (rows: Session[], empty: string) =>
    rows.length === 0 ? (
      <EmptyState text={empty} />
    ) : (
      <ul className="rounded-[20px] bg-card px-4">
        {rows.map((event) => <CalendarRow key={event.id} event={event} response={responseByEvent.get(event.id)} />)}
      </ul>
    );

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <SectionHead title={t("calendar.upcoming")} count={upcoming.length} />
        {list(upcoming, t("calendar.emptyUpcoming"))}
      </section>
      <section className="space-y-3">
        <SectionHead title={t("calendar.past")} />
        {list(recent, t("calendar.emptyPast"))}
      </section>
    </div>
  );
}

function CalendarRow({ event, response }: { event: Session; response: SessionResponse | undefined }) {
  const { t } = useTranslation("playerArea");
  const time = new Date(event.starts_at).toLocaleTimeString(currentIntlLocale(), { hour: "2-digit", minute: "2-digit" });
  return (
    <li className="flex items-center gap-3 py-3 border-t first:border-t-0">
      <DateBlock iso={event.starts_at} />
      <div className="min-w-0 flex-1">
        <p className="font-medium truncate">{event.title}</p>
        <p className="text-[13px] text-[hsl(var(--faint))] truncate">
          {event.has_matches ? t("session.matchDay") : t("session.training")} · {time}
          {event.location ? ` · ${event.location}` : ""}
        </p>
      </div>
      <StatusTag status={response?.status || "no_response"} />
    </li>
  );
}

function PresenceBar({ label, done, total }: { label: string; done: number; total: number }) {
  const { t } = useTranslation("playerArea");
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium tabular-nums">{t("selfStats.outOf", { done, total })}</span>
      </div>
      <div className="bar-stack mt-1.5">
        <div className="h-full bg-[hsl(var(--ok))] rounded-[3px]" style={{ width: `${total > 0 ? Math.min(100, (done / total) * 100) : 0}%` }} />
      </div>
    </div>
  );
}

function PlayerSelfStatsTab({
  matchStats,
  present,
  late,
  absent,
  noResponse,
  goals,
  assists,
  starts,
  matchPresent,
  trainingPresent,
  matchSessions,
  trainingSessions,
}: {
  matchStats: MatchPlayerStat[];
  present: number;
  late: number;
  absent: number;
  noResponse: number;
  goals: number;
  assists: number;
  starts: number;
  matchPresent: number;
  trainingPresent: number;
  matchSessions: number;
  trainingSessions: number;
}) {
  const { t } = useTranslation("playerArea");
  const recentMatches = [...matchStats]
    .sort((a, b) => String(b.match_starts_at || "").localeCompare(String(a.match_starts_at || "")))
    .slice(0, 15);
  const scoreTone = (us: number, them: number) => (us > them ? "text-[hsl(var(--ok))]" : us < them ? "text-[hsl(var(--danger))]" : "");

  return (
    <div className="space-y-4">
      <dl className="rounded-[20px] bg-card p-5 grid grid-cols-4 gap-2">
        {([[t("selfStats.goals"), goals, "text-[hsl(var(--ok))]"], [t("selfStats.assists"), assists, ""], [t("selfStats.matches"), matchStats.length, ""], [t("selfStats.starts"), starts, ""]] as const).map(([label, value, tone]) => (
          <div key={label} className="min-w-0">
            <dd className={`eb-num text-[44px] pr-1 ${tone}`}>{value}</dd>
            <dt className="text-[13px] text-[hsl(var(--faint))] truncate">{label}</dt>
          </div>
        ))}
      </dl>

      <section className="rounded-[20px] bg-card p-5 space-y-3">
        <h2 className="text-[17px] font-semibold">{t("selfStats.presencesTitle")}</h2>
        <PresenceBar label={t("selfStats.matchPresences")} done={matchPresent} total={matchSessions} />
        <PresenceBar label={t("selfStats.trainingPresences")} done={trainingPresent} total={trainingSessions} />
      </section>

      <section className="rounded-[20px] bg-card p-5">
        <h2 className="text-[17px] font-semibold">{t("selfStats.respTitle")}</h2>
        <dl className="mt-3 grid grid-cols-4 gap-2">
          {([[t("selfStats.respPresent"), present, "dot-ok"], [t("selfStats.respLate"), late, "dot-warn"], [t("selfStats.respAbsent"), absent, "dot-danger"], [t("selfStats.respNoResponse"), noResponse, "dot-muted"]] as const).map(([label, value, dot]) => (
            <div key={label} className="min-w-0">
              <dd className="flex items-center gap-1.5"><span aria-hidden className={`eb-dot ${dot}`} /><span className="eb-num text-[32px] pr-1">{value}</span></dd>
              <dt className="text-[13px] text-[hsl(var(--faint))] truncate">{label}</dt>
            </div>
          ))}
        </dl>
      </section>

      <section className="space-y-3">
        <SectionHead title={t("selfStats.recentMatches")} />
        {recentMatches.length === 0 ? (
          <EmptyState text={t("selfStats.noMatchesPlayed")} />
        ) : (
          <ul className="rounded-[20px] bg-card px-4">
            {recentMatches.map((stat) => {
              const scored = stat.match_our_score != null && stat.match_opponent_score != null;
              return (
                <li key={stat.id} className="flex items-center gap-3 py-3 border-t first:border-t-0">
                  <div className="min-w-0 flex-1">
                    <p className="eb-data text-[13px] text-[hsl(var(--faint))]">{stat.match_starts_at ? formatDateTime(stat.match_starts_at) : t("selfStats.dateNa")}</p>
                    <p className="font-medium truncate">{stat.match_opponent_name || t("selfStats.fallbackMatch")}</p>
                    <p className="text-[13px] text-[hsl(var(--faint))] truncate">{stat.goals || 0} G · {stat.assists || 0} A{stat.started ? t("selfStats.starter") : ""}</p>
                  </div>
                  {scored && (
                    <span className={`eb-num text-[34px] pr-1 shrink-0 ${scoreTone(stat.match_our_score!, stat.match_opponent_score!)}`}>
                      {stat.match_our_score}-{stat.match_opponent_score}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function PlayerProfileTab({
  player,
  email,
  userId,
  role,
  team,
  onCreateNewTeam,
}: {
  player: Player;
  email: string;
  userId: string;
  role: string;
  team: Team;
  onCreateNewTeam?: () => void;
}) {
  const { t } = useTranslation("playerArea");
  const fields: Array<[string, string]> = [
    [t("profile.role"), player.role_code || "-"],
    [t("profile.eaSportsId"), player.ea_sports_id || "-"],
    [t("profile.proClubsHandle"), player.ea_account_handle || "-"],
    [t("profile.email"), email || "-"],
    [t("profile.phone"), player.phone || "-"],
  ];
  return (
    <div className="space-y-4">
      <section className="rounded-[20px] bg-card p-5 flex items-center gap-4">
        <span className="eb-num text-[56px] pr-1 w-16 shrink-0 text-center">{player.shirt_number ?? "-"}</span>
        <div className="min-w-0 flex-1">
          <h2 className="text-[22px] font-bold leading-tight truncate">{player.display_name}</h2>
          <p className="text-[13px] text-[hsl(var(--faint))] truncate">
            {player.is_guest ? t("profile.guest") : teamRoleLabel(role) || t("profile.player")} · {team.name}
          </p>
        </div>
      </section>

      <section className="rounded-[20px] bg-card px-5 pt-5 pb-2">
        <h2 className="text-[17px] font-semibold">{t("profile.personalTitle")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("profile.personalDesc")}</p>
        <dl className="mt-2">
          {fields.map(([label, value]) => (
            <div key={label} className="flex items-baseline justify-between gap-4 py-2.5 border-t first:border-t-0">
              <dt className="text-sm text-muted-foreground shrink-0">{label}</dt>
              <dd className="min-w-0 truncate text-right">{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <PushSubscribeManager />

      <AccountCard currentEmail={email} />

      <MyTeamsCard userId={userId} onCreateNewTeam={onCreateNewTeam} />
    </div>
  );
}

function PlayerEaPersonalTab({ teamId, player }: { teamId: string; player: Player }) {
  const { t } = useTranslation("playerArea");
  const eaCandidates = buildPlayerEaCandidates(player);
  return (
    <div className="space-y-2 animate-fade-in">
      <div className="flex items-center gap-2 px-1 mb-2">
        <FcIcon className="h-4 w-4 text-accent" />
        <h2 className="text-lg font-semibold tracking-tight">{t("myStats")}</h2>
      </div>
      <PlayerEaTab
        teamId={teamId}
        eaCandidates={eaCandidates}
        emptyText={t("noEa")}
        playerId={player.id}
        displayName={player.display_name}
        roleCode={player.role_code}
      />
    </div>
  );
}

function UnlinkedPlayerScreen({
  team,
  session,
  onSignOut,
}: {
  team: Team;
  session: AuthSession;
  onSignOut: () => void;
}) {
  const { t } = useTranslation("playerArea");
  const meta = (session.user.user_metadata || {}) as Record<string, string>;

  return (
    <AuthShell>
      <div className="flex items-center gap-3">
        <TeamCrest team={team} size={36} />
        <p className="text-[13px] text-[hsl(var(--faint))] min-w-0 truncate">{team.name}</p>
      </div>
      <h1 className="mt-4 text-[26px] font-bold leading-tight">{t("unlinked.title")}</h1>
      <p className="mt-2 text-muted-foreground">
        <Trans
          i18nKey="unlinked.subtitle"
          t={t}
          values={{ teamName: team.name }}
          components={{ strong: <strong /> }}
        />
      </p>
      <div className="mt-5 space-y-3 text-sm">
        {/* Lista "Sei tu?": l'utente sceglie il proprio nome dalla rosa,
            il founder approva. Stesso flusso di JoinTeamPage. */}
        <PickYourselfStep
          teamId={team.id}
          teamName={team.name}
          firstNameHint={meta.first_name || ""}
          lastNameHint={meta.last_name || ""}
          hideHeader
        />
        <p className="text-[13px] text-[hsl(var(--faint))] leading-snug pt-3 border-t">{t("unlinked.fallback")}</p>
        <Button variant="outline" className="w-full" onClick={onSignOut}>
          <LogOut />
          {t("unlinked.signOut")}
        </Button>
      </div>
    </AuthShell>
  );
}
